import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
  OnDestroy,
  OnInit,
  HostListener,
  ChangeDetectionStrategy,
  WritableSignal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  FormGroup,
} from '@angular/forms';
import {
  NavigationStart,
  Router,
  ActivatedRoute,
  RouterModule,
} from '@angular/router';
import {
  RdsButtonModule,
  RdsCheckboxModule,
  RdsChipEditedEvent,
  RdsChipInputEvent,
  RdsChipsModule,
  RdsDialogService,
  RdsDropdownModule,
  RdsFormFieldModule,
  RdsIconsModule,
  RdsLinkModule,
  RdsListItemModule,
  RdsMultiAutocompleteComponent,
  RdsProgressSpinnerComponent,
  RdsSingleSelectFilterByFunc,
  RdsSingleSelectOptionComponent,
  RdsStepperModule,
  RdsTableDataSource,
  RdsTableModule,
  RdsUploadModule,
  RdsBadgeModule,
} from '@rds/angular-components';
import { OfferingType } from '../../../enums/offering-type.enum';
import { ApplicationType } from '../../../enums/application-type.enum';
import { Classification } from '../../../enums/classification.enum';
import { OfferingsService } from '../../../services/offerings.service';
import { MulesoftOffering } from '../../../models/mulesoft-offering';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, filter, takeUntil, debounceTime } from 'rxjs/operators';
import { Subject, merge } from 'rxjs';
import { mapMulesoftOfferingToApplicationForm } from '../../../mappers/mulesoft-offering-form.mapper';
import { buildMulesoftApplicationDTO } from '../../../mappers/mulesoft.mapper';
import { MulesoftService } from '../../../services/mulesoft.service';
import { Frequency } from '../../../enums/frequency.enum';
import { ApprovalStatus } from '../../../enums/approval-status.enum';
import { UiService } from '../../../services/ui.service';
import { Currency } from '../../../enums/currency.enum';
import { LicenseCostResponsibility } from '../../../enums/license-cost-responsibility.enum';
import {
  OfferingFeature,
  OfferingUseCase,
  OfferingInstallation,
  Offering,
  OfferingSupportChannel,
  OfferingSupportHour,
  OfferingCommunity,
  OfferingHosting,
  OfferingAgent,
} from '../../../models/offering';
import { APPLICATION_FORM_TEXTS } from '../../../constants/application-form.constants';
import { BUTTON_TEXTS } from '../../../constants/buttons.constants';
import { ERRORS_TEXTS } from '../../../constants/errors.contstants';
import {
  decimalValidator,
  strictEmailValidator,
  emailListValidator,
  slugValidator,
  slugify,
} from '../../../validators';
import { AnalyticsService } from '../../../services/analytics.service';
import { FeatureFlagDirective } from '../../../directives/feature-flag.directive';
import { CommonModule } from '@angular/common';
import { noDuplicateEmailsValidator } from '../../../validators/no-duplicate-emails.validator';
import { POPUPS } from '../../../constants/popups.constants';
import { isDuplicateNameError } from '../../../util/duplicate-name-error.util';
import { attachDirtyDialogGuard } from '../../../guards/dirty-dialog.guard';
import { FormSkeletonComponent } from '../form-skeleton/form-skeleton.component';

@Component({
  selector: 'inexp-focus-application-form',
  standalone: true,
  imports: [
    RdsIconsModule,
    RdsButtonModule,
    RdsStepperModule,
    ReactiveFormsModule,
    RdsFormFieldModule,
    RdsUploadModule,
    RdsCheckboxModule,
    RdsTableModule,
    RdsDropdownModule,
    RdsListItemModule,
    RdsChipsModule,
    RdsLinkModule,
    RouterModule,
    RdsProgressSpinnerComponent,
    FeatureFlagDirective,
    RdsMultiAutocompleteComponent,
    CommonModule,
    RdsBadgeModule,
    FormSkeletonComponent,
  ],
  templateUrl: './focus-application-form.component.html',
  styleUrl: './focus-application-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FocusApplicationFormComponent implements OnInit, OnDestroy {
  // Services
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _offeringsService = inject(OfferingsService);
  private _offeringLoaded = false;
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _mulesoftService = inject(MulesoftService);
  private readonly _uiService = inject(UiService);
  private readonly _dialog = inject(RdsDialogService);
  private readonly _analyticsService = inject(AnalyticsService);
  // UI
  private readonly _fieldErrorHistory = new Map<string, number>();
  /** The draft offering fetched for editing (direct by-uuid fetch). */
  readonly draftOffering = signal<MulesoftOffering | undefined>(undefined);
  readonly offering = computed(() => this.draftOffering());
  /** Edit mode + draft offering not yet loaded → show form skeleton. */
  readonly isLoading = computed(() => !!this.offeringUUID() && !this.draftOffering());

  // Validation Errors
  private validationErrors: string[] = [];

  // Cleanup
  private readonly destroy$ = new Subject<void>();

  // Data sources
  readonly featureDataSource = new RdsTableDataSource<OfferingFeature>().init();
  readonly useCaseDataSource = new RdsTableDataSource<OfferingUseCase>().init();
  readonly installationDataSource =
    new RdsTableDataSource<OfferingInstallation>().init();
  readonly supportChannelDataSource =
    new RdsTableDataSource<OfferingSupportChannel>().init();
  readonly supportHourDataSource =
    new RdsTableDataSource<OfferingSupportHour>().init();
  readonly communityDataSource =
    new RdsTableDataSource<OfferingCommunity>().init();
  readonly hostingDataSource = new RdsTableDataSource<OfferingHosting>().init();
  readonly agentDataSource = new RdsTableDataSource<OfferingAgent>().init();

  // Constants
  readonly offeringTypes = Object.values(OfferingType);
  readonly applicationTypes = Object.values(ApplicationType);
  readonly classifications = Object.values(Classification);
  readonly frequencies = Object.values(Frequency);
  readonly approvalStatuses = Object.values(ApprovalStatus);
  readonly currencies = Object.values(Currency);
  readonly licenseCostResponsibilities = Object.values(
    LicenseCostResponsibility,
  );
  readonly ApprovalStatus = ApprovalStatus;
  readonly OFFERING_FORM = APPLICATION_FORM_TEXTS;
  readonly BUTTONS = BUTTON_TEXTS;
  readonly ERRORS = ERRORS_TEXTS;
  readonly POPUPS = POPUPS;

  readonly featureColumns = ['name', 'description', 'benefit'];
  readonly useCaseColumns = ['name', 'description'];
  readonly installationColumns = ['applicationType', 'instructions', 'url'];
  readonly supportChannelColumns = [
    'channelType',
    'destinationValue',
    'description',
    'serviceDeskName',
  ];
  readonly supportHourColumns = [
    'days',
    'startTime',
    'endTime',
    'timezone',
    'responseTime',
  ];
  readonly communityColumns = ['title', 'description', 'url'];
  readonly hostingColumns = ['name', 'type', 'managed'];
  readonly agentColumns = ['name', 'url'];

  // Form abandon tracking
  private isFormAbandonNavigateLogged = false;
  private isFormAbandonCloseLogged = false;
  private isFormSavedOrPublished = false;
  isPublishing = signal(false);
  isDraftSaving = signal(false);

  // Signals
  tags = signal<string[]>([]);
  authors = signal<string[]>([]);
  features = signal<OfferingFeature[]>([]);
  useCases = signal<OfferingUseCase[]>([]);
  installations = signal<OfferingInstallation[]>([]);
  supportChannels = signal<OfferingSupportChannel[]>([]);
  supportHours = signal<OfferingSupportHour[]>([]);
  communities = signal<OfferingCommunity[]>([]);
  hosting = signal<OfferingHosting[]>([]);
  agents = signal<OfferingAgent[]>([]);
  offerings = this._offeringsService.offerings;
  imagePreview = signal<string | null>(null);
  offeringId = signal<string>('');
  offeringSlug = signal<string>('');
  offeringName = signal<string>('');
  knowledge = signal<string>('');
  hasUserInteracted = signal(false);
  similarOfferingUuidsFromMule = signal<string[]>([]);

  // Route parameter
  offeringUUID = toSignal(
    this._route.paramMap.pipe(map((params) => params.get('uuid'))),
    { initialValue: this._route.snapshot.paramMap.get('uuid') },
  );

  // Form entry analytics + create-mode initialisation (runs once when uuid resolves)
  private readonly logAndLoad = effect(() => {
    const offeringUUID = this.offeringUUID();

    if (!offeringUUID) {
      this.setInitialFormValues();
    }

    // Event: form_entry
    this._analyticsService.pushFormEntryEvent(
      offeringUUID ?? '',
      OfferingType.APPLICATION,
    );
  });

  // Fetch the offering for editing directly by uuid once the route resolves.
  private readonly loadOfferingEffect = effect(() => {
    const offeringUUID = this.offeringUUID();
    if (!offeringUUID || this._offeringLoaded) return;

    this._offeringLoaded = true;
    this.loadOfferingById(offeringUUID);
  });

  // Fetch a single draft offering and patch the form with it.
  private loadOfferingById(uuid: string): void {
    this._offeringsService
      .fetchDraftOffering(uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((offering) => {
        if (!offering) return;
        this.draftOffering.set(offering);
        this.loadOffering(offering);
      });
  }

  /**
   * After a successful write, overlay the authoritative response onto the loaded
   * offering (ids, slug, timestamps) instead of re-reading — the read endpoint is
   * cache-lagged right after a write and would revert the form to stale data. The
   * form already holds the saved values, so form controls are left untouched.
   */
  private syncAfterSave(response: { offering?: MulesoftOffering } | null): void {
    const saved = response?.offering;
    if (!saved) return;
    this.draftOffering.update((prev) => (prev ? { ...prev, ...saved } : saved));
    // Keep the just-saved offering visible in "my offerings" despite the
    // cache-lagged list refresh.
    this._offeringsService.upsertDraftOptimistic(this.draftOffering());
  }

  private readonly syncSimilarOfferingsEffect = effect(() => {
    const offerings = this.offerings();
    const uuids = this.similarOfferingUuidsFromMule();

    // Wait until both are available
    if (!offerings.length || !uuids.length) return;

    // map UUIDs to Offering objects
    const mappedOfferings = uuids
      .map((ref) => offerings.find((o) => String(o.id) === ref || o.uuid === ref))
      .filter((o): o is Offering => !!o);

    const currentValue = this.formOverview.getRawValue().similarOfferings;

    // avoid unnecessary patch
    if (
      Array.isArray(currentValue) &&
      currentValue.length === mappedOfferings.length &&
      currentValue.every((o: Offering) =>
        mappedOfferings.some((m) => m.uuid === o.uuid),
      )
    ) {
      return;
    }

    // Patch form control with the mapped full Offering objects
    this.formOverview.patchValue({ similarOfferings: mappedOfferings });
    this.formOverview.get('similarOfferings')?.markAsPristine();

    // Update original form values so the resolved offerings are not seen as a user change
    this._analyticsService.setOriginalFormValues({
      formOverview: this.formOverview.getRawValue(),
      formDetails: this.formDetails.getRawValue(),
      formFunctionality: this.formFunctionality.getRawValue(),
      formPermissions: this.formPermissions.getRawValue(),
    });
  });

  // Form groups
  formOverview = this._fb.group({
    displayName: ['', [Validators.required]],
    approvalStatus: ['', Validators.required],
    successor: [''],
    description: ['', Validators.required],
    similarOfferings: [[] as Offering[]],
  });

  formDetails = this._fb.group({
    systemRiskAssessmentName: ['', Validators.required],
    classificationLevel: ['', Validators.required],
    classificationRetentionPeriod: [''],
    gxp: ['', Validators.required],
    isPerUserPricingAvailable: [false],
    perUserCostValue: ['', decimalValidator()],
    perUserCostCurrency: [''],
    perUserCostFrequency: [''],
    isAdditionalCostConsiderationsAvailable: [false],
    additionalCostConsiderations: [''],
    isOtherPricingModelsAvailable: [false],
    otherPricingModels: [''],
    licenseCostResponsibility: ['', Validators.required],
    hosting: [[] as OfferingHosting[]],
    isSingleSignOnSupported: [false],
    singleSignOnProviders: [''],
    agents: [[] as OfferingAgent[]],
    communities: [[] as OfferingCommunity[]],
    isSupported: ['', Validators.required],
    supportChannels: [[] as OfferingSupportChannel[]],
    supportHours: [[] as OfferingSupportHour[]],
    installationInformation: [[] as OfferingInstallation[]],
  });

  formFunctionality = this._fb.group({
    features: [[] as OfferingFeature[]],
    useCases: [[] as OfferingUseCase[]],
  });

  formPermissions = this._fb.group(
    {
      ownerEmail: ['', [Validators.required, strictEmailValidator()]],
      deputyEmail: ['', [Validators.required, strictEmailValidator()]],
      authorEmails: [
        [] as string[],
        [Validators.required, emailListValidator()],
      ],
    },
    {
      validators: [noDuplicateEmailsValidator()],
    },
  );

  // Computed summary (only if needed in template)
  readonly summary = computed(() => ({
    overview: this.overviewSig(),
    details: this.detailsSig(),
    permissions: this.permissionsSig(),
    features: this.features(),
    useCases: this.useCases(),
    supportChannels: this.supportChannels(),
    supportHours: this.supportHours(),
    communities: this.communities(),
    installations: this.installations(),
  }));

  // Form value signals
  readonly overviewSig = toSignal(
    this.formOverview.valueChanges.pipe(
      debounceTime(300),
      map(() => this.formOverview.getRawValue()),
    ),
    { initialValue: this.formOverview.getRawValue() },
  );

  readonly detailsSig = toSignal(
    this.formDetails.valueChanges.pipe(
      debounceTime(300),
      map(() => this.formDetails.getRawValue()),
    ),
    { initialValue: this.formDetails.getRawValue() },
  );

  readonly permissionsSig = toSignal(
    this.formPermissions.valueChanges.pipe(
      debounceTime(300),
      map(() => this.formPermissions.getRawValue()),
    ),
    { initialValue: this.formPermissions.getRawValue() },
  );

  ngOnInit(): void {
    this.setupRouteChangeTracking();
    this.setupCacheInvalidation();
    this.setupConditionalValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._analyticsService.reset();
  }

  // Setup methods
  private setupConditionalValidation(): void {
    // Define all conditional field relationships
    const conditionalFields = [
      {
        checkboxControl: 'isPerUserPricingAvailable',
        dependentControl: 'perUserCostValue',
        validators: [Validators.required, decimalValidator()],
      },
      {
        checkboxControl: 'isPerUserPricingAvailable',
        dependentControl: 'perUserCostCurrency',
        validators: [Validators.required],
      },
      {
        checkboxControl: 'isPerUserPricingAvailable',
        dependentControl: 'perUserCostFrequency',
        validators: [Validators.required],
      },
      {
        checkboxControl: 'isAdditionalCostConsiderationsAvailable',
        dependentControl: 'additionalCostConsiderations',
        validators: [Validators.required],
      },
      {
        checkboxControl: 'isOtherPricingModelsAvailable',
        dependentControl: 'otherPricingModels',
        validators: [Validators.required],
      },
      {
        checkboxControl: 'isSingleSignOnSupported',
        dependentControl: 'singleSignOnProviders',
        validators: [Validators.required],
      },
    ];

    // Set up validation for each conditional field
    for (const {
      checkboxControl,
      dependentControl,
      validators,
    } of conditionalFields) {
      this.setupConditionalField(checkboxControl, dependentControl, validators);
    }
  }

  private setupConditionalField(
    checkboxControlName: string,
    dependentControlName: string,
    validators: any[],
  ): void {
    this.formDetails
      .get(checkboxControlName)
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isChecked) => {
        const dependentControl = this.formDetails.get(dependentControlName);

        if (isChecked) {
          dependentControl?.setValidators(validators);
        } else {
          dependentControl?.clearValidators();
          dependentControl?.setValue('');
        }

        dependentControl?.updateValueAndValidity();
      });
  }

  readonly getOfferingById = computed(() => {
    const offeringsMap = new Map(
      this.offerings().map((offering) => [offering.uuid, offering]),
    );

    return (id: string | null | undefined) => {
      if (!id) return null;
      return offeringsMap.get(id);
    };
  });

  readonly similarOfferingOptions = (): Offering[] => {
    // Return all offerings, excluding the current one to avoid self-selection
    const currentUuid = this.offeringUUID();
    return this.offerings().filter((o) => o.uuid !== currentUuid);
  };

  readonly compareOfferings = (o1: Offering, o2: Offering): boolean => {
    if (!o1 || !o2) return false;
    return o1.uuid === o2.uuid;
  };

  readonly filterOfferings = (offering: Offering, query: string): boolean => {
    if (!query) return true; // show all if empty
    return offering.displayName.toLowerCase().includes(query.toLowerCase());
  };

  // UI summary: comma-separated display names
  readonly similarOfferingsDisplay = computed((): string => {
    const similarOfferingsRaw = this.summary().overview.similarOfferings;
    let similarOfferingsArray: any[];
    if (Array.isArray(similarOfferingsRaw)) {
      similarOfferingsArray = similarOfferingsRaw;
    } else if (similarOfferingsRaw) {
      similarOfferingsArray = Object.values(similarOfferingsRaw);
    } else {
      similarOfferingsArray = [];
    }

    const names = similarOfferingsArray
      .filter(
        (o): o is Offering =>
          !!o && typeof o === 'object' && 'displayName' in o,
      )
      .map((o) => o.displayName)
      .filter(Boolean);

    return names.length ? names.join(', ') : '—';
  });

  // Array of UUIDs for backend
  readonly similarOfferingsUuids = computed((): string[] => {
    const similarOfferingsRaw = this.summary().overview.similarOfferings;

    // Ensure we have an array: handles arrays, objects, null, undefined
    let similarOfferingsArray: any[];
    if (Array.isArray(similarOfferingsRaw)) {
      similarOfferingsArray = similarOfferingsRaw;
    } else if (similarOfferingsRaw) {
      similarOfferingsArray = Object.values(similarOfferingsRaw);
    } else {
      similarOfferingsArray = [];
    }

    // Only keep valid Offering objects with a uuid
    return similarOfferingsArray
      .filter((o): o is Offering => !!o && typeof o === 'object' && 'uuid' in o)
      .map((o) => o.uuid);
  });

  private setupRouteChangeTracking(): void {
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.trackFormAbandonNavigate());
  }

  private setupCacheInvalidation(): void {
    merge(
      this.formOverview.valueChanges,
      this.formDetails.valueChanges,
      this.formFunctionality.valueChanges,
      this.formPermissions.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this._analyticsService.invalidateCache();
      });
  }

  // Loading offering
  loadOffering(sourceOffering: MulesoftOffering): void {
    try {
        const offering = mapMulesoftOfferingToApplicationForm(sourceOffering);
        this.offeringId.set(offering.id);
        this.knowledge.set(offering.knowledge);
        this.offeringSlug.set(sourceOffering.slug ?? '');
        this.offeringName.set(
          sourceOffering.offeringName ?? sourceOffering.displayName ?? '',
        );

        // Set signals FIRST
        this.tags.set(offering.overview.tags ?? []);
        this.authors.set(offering.permissions.authorEmails ?? []);
        this.installations.set(offering.details.installationInformation ?? []);
        this.features.set(offering.functionality.features ?? []);
        this.useCases.set(offering.functionality.useCases ?? []);
        this.supportChannels.set(offering.details.supportChannels ?? []);
        this.supportHours.set(offering.details.supportHours ?? []);
        this.communities.set(offering.details.communities ?? []);
        this.hosting.set(offering.details.hosting ?? []);
        this.agents.set(offering.details.agents ?? []);

        this.similarOfferingUuidsFromMule.set(
          offering.overview.similarOfferings ?? [],
        );

        // Then patch forms with the SAME data including synced arrays
        this.formOverview.patchValue({
          ...offering.overview,
          tags: this.tags(),
        });
        this.formDetails.patchValue(offering.details);
        this.formFunctionality.patchValue({
          features: offering.functionality.features ?? [],
          useCases: offering.functionality.useCases ?? [],
        });
        this.formPermissions.patchValue({
          ...offering.permissions,
          authorEmails: this.authors(),
        });

        this.installationDataSource.data = this.installations();
        this.featureDataSource.data = this.features();
        this.useCaseDataSource.data = this.useCases();
        this.supportChannelDataSource.data = this.supportChannels();
        this.supportHourDataSource.data = this.supportHours();
        this.communityDataSource.data = this.communities();
        this.hostingDataSource.data = this.hosting();
        this.agentDataSource.data = this.agents();

        // Store the initial form values for comparison
        this._analyticsService.setOriginalFormValues({
          formOverview: this.formOverview.getRawValue(),
          formDetails: this.formDetails.getRawValue(),
          formFunctionality: this.formFunctionality.getRawValue(),
          formPermissions: this.formPermissions.getRawValue(),
        });

        this.formOverview.markAsPristine();
        this.formDetails.markAsPristine();
        this.formFunctionality.markAsPristine();
        this.formPermissions.markAsPristine();
        this.hasUserInteracted.set(false);
    } catch {
      this._uiService.showErrorAlert('Error', 'Failed to load offering data');
      this._router.navigate(['/focus/my-offerings']);
    }
  }

  // Generic dialog handler to reduce code duplication
  private async handleDialog<T>(
    componentLoader: () => Promise<any>,
    dataKey: string,
    signal: WritableSignal<T[]>,
    formControlName: string,
    dataSource: RdsTableDataSource<T>,
    item?: T,
    additionalData?: any,
  ): Promise<void> {
    try {
      const componentModule = await componentLoader();
      const Component = componentModule[Object.keys(componentModule)[0]];

      const dialogData: any = { [dataKey]: item };
      if (additionalData) {
        Object.assign(dialogData, additionalData);
      }

      const dialogRef = this._dialog.open(Component, {
        size: 'm',
        data: dialogData,
        disableClose: true,
      });

      attachDirtyDialogGuard(dialogRef);

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            if (data) {
              signal.update((arr) => {
                if (item) {
                  // Edit existing item
                  const index = arr.indexOf(item);
                  if (index > -1) {
                    return [
                      ...arr.slice(0, index),
                      { ...item, ...data },
                      ...arr.slice(index + 1),
                    ];
                  }
                  return arr;
                } else {
                  // Add new item
                  return [...arr, data];
                }
              });

              // Update form control
              const control = this.getFormControl(formControlName);
              if (control) {
                control.patchValue(signal());
              }

              dataSource.data = signal();
            }
          },
        });
    } catch (error) {
      console.error('Error loading dialog component:', error);
    }
  }

  private getFormControl(path: string): AbstractControl | null {
    if (path.startsWith('formDetails.')) {
      return this.formDetails.get(path.replace('formDetails.', ''));
    } else if (path.startsWith('formFunctionality.')) {
      return this.formFunctionality.get(path.replace('formFunctionality.', ''));
    }
    return null;
  }

  // Dialog handlers
  async handleFeatureDialog(feature?: OfferingFeature): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-feature-dialog/focus-feature-dialog.component'),
      'feature',
      this.features,
      'formFunctionality.features',
      this.featureDataSource,
      feature,
    );
  }

  async handleUseCaseDialog(useCase?: OfferingUseCase): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-use-case-dialog/focus-use-case-dialog.component'),
      'useCase',
      this.useCases,
      'formFunctionality.useCases',
      this.useCaseDataSource,
      useCase,
    );
  }

  async handleInstallationDialog(
    installation?: OfferingInstallation,
  ): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-installation-dialog/focus-installation-dialog.component'),
      'installation',
      this.installations,
      'formDetails.installationInformation',
      this.installationDataSource,
      installation,
      { applicationTypes: this.applicationTypes },
    );
  }

  // Delete handlers
  handleDeleteFeature(feature: OfferingFeature): void {
    this.deleteItem(
      this.features,
      feature,
      this.formFunctionality.get('features'),
      this.featureDataSource,
    );
  }

  handleDeleteUseCase(useCase: OfferingUseCase): void {
    this.deleteItem(
      this.useCases,
      useCase,
      this.formFunctionality.get('useCases'),
      this.useCaseDataSource,
    );
  }

  handleDeleteInstallation(installation: OfferingInstallation): void {
    this.deleteItem(
      this.installations,
      installation,
      this.formDetails.get('installationInformation'),
      this.installationDataSource,
    );
  }

  async handleSupportChannelDialog(sc?: OfferingSupportChannel): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-support-channel-dialog/focus-support-channel-dialog.component'),
      'supportChannel',
      this.supportChannels,
      'formDetails.supportChannels',
      this.supportChannelDataSource,
      sc,
    );
  }

  async handleSupportHourDialog(sh?: OfferingSupportHour): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-support-hours-dialog/focus-support-hours-dialog.component'),
      'supportHour',
      this.supportHours,
      'formDetails.supportHours',
      this.supportHourDataSource,
      sh,
      { existingSupportHours: this.supportHours().filter((h) => h !== sh) },
    );
  }

  handleDeleteSupportChannel(sc: OfferingSupportChannel): void {
    this.deleteItem(
      this.supportChannels,
      sc,
      this.formDetails.get('supportChannels'),
      this.supportChannelDataSource,
    );
  }

  handleDeleteSupportHour(sh: OfferingSupportHour): void {
    this.deleteItem(
      this.supportHours,
      sh,
      this.formDetails.get('supportHours'),
      this.supportHourDataSource,
    );
  }

  async handleCommunityDialog(community?: OfferingCommunity): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-community-dialog/focus-community-dialog.component'),
      'community',
      this.communities,
      'formDetails.communities',
      this.communityDataSource,
      community,
    );
  }

  handleDeleteCommunity(community: OfferingCommunity): void {
    this.deleteItem(
      this.communities,
      community,
      this.formDetails.get('communities'),
      this.communityDataSource,
    );
  }

  async handlehostingDialog(hi?: OfferingHosting): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-hosting-information-dialog/focus-hosting-information-dialog.component'),
      'hosting',
      this.hosting,
      'formDetails.hosting',
      this.hostingDataSource,
      hi,
    );
  }

  handleDeletehosting(hi: OfferingHosting): void {
    this.deleteItem(
      this.hosting,
      hi,
      this.formDetails.get('hosting'),
      this.hostingDataSource,
    );
  }

  async handleAgentDialog(agent?: OfferingAgent): Promise<void> {
    await this.handleDialog(
      () =>
        import('../dialogs/focus-agent-dialog/focus-agent-dialog.component'),
      'agent',
      this.agents,
      'formDetails.agents',
      this.agentDataSource,
      agent,
    );
  }

  handleDeleteAgent(agent: OfferingAgent): void {
    this.deleteItem(
      this.agents,
      agent,
      this.formDetails.get('agents'),
      this.agentDataSource,
    );
  }

  private deleteItem<T>(
    signal: WritableSignal<T[]>,
    item: T,
    formControl: AbstractControl | null,
    dataSource: RdsTableDataSource<T>,
  ): void {
    if (!formControl) return;
    this._uiService
      .showDeleteDialog(
        POPUPS.DELETE.TITLE,
        POPUPS.DELETE.DESCRIPTION,
        POPUPS.DELETE.WARNING,
        BUTTON_TEXTS.DELETE,
        BUTTON_TEXTS.CANCEL,
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          signal.update((arr) => arr.filter((i) => i !== item));
          formControl.patchValue(signal());
          dataSource.data = signal();
        }
      });
  }

  private getCurrentFormValues(): any {
    return {
      formOverview: this.formOverview.getRawValue(),
      formDetails: this.formDetails.getRawValue(),
      formFunctionality: this.formFunctionality.getRawValue(),
      formPermissions: this.formPermissions.getRawValue(),
    };
  }

  private getAllFormControls(): Record<string, AbstractControl> {
    return {
      ...this.formOverview.controls,
      ...this.formDetails.controls,
      ...this.formFunctionality.controls,
      ...this.formPermissions.controls,
    };
  }

  // Call this method to update error history
  private updateErrorHistory(): void {
    const controls = this.getAllFormControls();

    for (const [fieldName, control] of Object.entries(controls)) {
      if (control.invalid && control.errors) {
        const currentCount = this._fieldErrorHistory.get(fieldName) ?? 0;
        this._fieldErrorHistory.set(fieldName, currentCount + 1);
      }
    }
  }

  // Analytics events
  handleSuccessEvent(): void {
    this._analyticsService.pushFormSuccessEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      OfferingType.APPLICATION,
    );
  }

  handleErrorEvent(): void {
    this.updateErrorHistory();
    this._analyticsService.pushFormErrorEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      this._fieldErrorHistory,
      OfferingType.APPLICATION,
    );
  }

  handleDraftEvent(): void {
    this.updateErrorHistory();
    this._analyticsService.pushDraftSaveEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      this._fieldErrorHistory,
      OfferingType.APPLICATION,
    );
  }

  private trackFormAbandonNavigate(): void {
    if (this.isFormAbandonNavigateLogged || this.isFormSavedOrPublished) {
      return;
    }
    this.isFormAbandonNavigateLogged = true;

    this._analyticsService.pushFormAbandonNavigateEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      OfferingType.APPLICATION,
    );
  }

  private trackFormAbandonClose(): void {
    if (this.isFormAbandonCloseLogged || this.isFormSavedOrPublished) {
      return;
    }
    this.isFormAbandonCloseLogged = true;

    this._analyticsService.pushFormAbandonCloseEvent(
      this.offeringId() ?? '',
      this.getCurrentFormValues(),
      this.getAllFormControls(),
      OfferingType.APPLICATION,
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): string | void {
    const currentValues = this.getCurrentFormValues();
    const changedFields =
      this._analyticsService['getChangedFields'](currentValues);

    if (changedFields.length > 0) {
      this.trackFormAbandonClose();
      $event.preventDefault();

      // Set returnValue only if it exists (for legacy browser support)
      if ('returnValue' in $event) {
        ($event as any).returnValue = '';
      }

      return '';
    }
  }

  canDeactivate(): boolean {
    if (this.isFormSavedOrPublished) {
      return true;
    }

    const currentValues = this.getCurrentFormValues();
    const changedFields =
      this._analyticsService['getChangedFields'](currentValues);
    if (changedFields.length > 0) {
      return confirm(
        'You have unpublished changes. Do you really want to leave without publishing?',
      );
    }
    return true;
  }

  markFormAsSavedOrPublished(): void {
    this.isFormSavedOrPublished = true;
  }

  onAuthorFocusOut(input: HTMLInputElement): void {
    const value = (input.value ?? '').trim().toLowerCase();
    if (!value) return;

    const currentAuthors: string[] = this.authors();
    if (currentAuthors.includes(value)) {
      this._uiService.showErrorToast(
        'Duplicate author email not allowed',
        'Error',
      );
      input.value = '';
      return;
    }

    this.authors.update((authors) => [...authors, value]);
    this.formPermissions.patchValue({ authorEmails: this.authors() });
    input.value = '';
  }

  getDuplicateEmailErrorMessage(
    fieldName: 'ownerEmail' | 'deputyEmail' | 'authorEmails',
  ): string {
    const owner = (this.formPermissions.get('ownerEmail')?.value ?? '')
      .trim()
      .toLowerCase();
    const deputy = (this.formPermissions.get('deputyEmail')?.value ?? '')
      .trim()
      .toLowerCase();
    const authors: string[] = (
      this.formPermissions.get('authorEmails')?.value ?? []
    ).map((a) => (a ?? '').trim().toLowerCase());

    if (!this.hasDuplicateError(fieldName)) return '';

    // Scenario 1: Email assigned to another role
    if (
      (fieldName === 'ownerEmail' &&
        owner &&
        (owner === deputy || authors.includes(owner))) ||
      (fieldName === 'deputyEmail' &&
        deputy &&
        (deputy === owner || authors.includes(deputy))) ||
      (fieldName === 'authorEmails' &&
        authors.some((a) => a && (a === owner || a === deputy)))
    ) {
      return this.OFFERING_FORM.PERMISSIONS.DUPLICATE_EMAIL_ERROR;
    }

    // Scenario 2: Email duplicate within the same role (authors)
    if (fieldName === 'authorEmails') {
      const duplicates = authors.filter((v, i, a) => a.indexOf(v) !== i);
      if (duplicates.length > 0) {
        return this.OFFERING_FORM.PERMISSIONS.AUTHOR.DUPLICATE_SAME_ROLE_ERROR;
      }
    }

    return this.OFFERING_FORM.PERMISSIONS.DUPLICATE_COMMON_EMAIL_ERROR;
  }

  private setInitialFormValues(): void {
    this._analyticsService.setOriginalFormValues({
      formOverview: this.formOverview.getRawValue(),
      formDetails: this.formDetails.getRawValue(),
      formFunctionality: this.formFunctionality.getRawValue(),
      formPermissions: this.formPermissions.getRawValue(),
    });
  }

  // Form submission
  handlePublish(): void {
    if (!this.validateAllForms()) {
      const missingFieldsList = this.validationErrors.join(', ');

      const detailedDescription = `${POPUPS.ERROR.DESCRIPTION} ${missingFieldsList}.`;
      this._uiService.showErrorAlert(
        POPUPS.ERROR.TITLE,
        detailedDescription,
      );
      this.handleErrorEvent();
      return;
    }

    const offeringUUID = this.offeringUUID();
    const offeringId = this.offeringId();
    const knowledge = this.knowledge();

    const mulesoftApplicationDTO = buildMulesoftApplicationDTO(
      offeringId,
      knowledge,
      this.formOverview,
      this.formDetails,
      this.formFunctionality,
      this.formPermissions,
      true,
    );

    this.isPublishing.set(true);

    const request$ = offeringUUID
      ? this._mulesoftService.editApplication(mulesoftApplicationDTO)
      : this._mulesoftService.createApplication(mulesoftApplicationDTO);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.isPublishing.set(false);
        this.formOverview.markAsPristine();
        this.formDetails.markAsPristine();
        this.formFunctionality.markAsPristine();
        this.formPermissions.markAsPristine();

        this._uiService.showSuccessAlert(
          'Success',
          'Your main offering content has been published!',
          '/focus/my-offerings',
          BUTTON_TEXTS.BACK_TO_MY_OFFERINGS,
          BUTTON_TEXTS.CLOSE,
        );

        this.handleSuccessEvent();
        this.markFormAsSavedOrPublished();
        if (offeringUUID) {
          // Do NOT re-read this offering — the read endpoint is cache-lagged
          // right after a write and would revert the form to stale data. Overlay
          // the authoritative write response onto the cached list instead; the
          // form already holds the saved values.
          this.syncAfterSave(response);
        } else if (response?.offering?.uuid) {
          this._offeringsService.upsertDraftOptimistic(response.offering);
          this._router.navigate(
            ['/focus/offering/application', response.offering.uuid],
            { replaceUrl: true },
          );
        }
      },
      error: (err) => {
        this.isPublishing.set(false);
        if (isDuplicateNameError(err)) {
          this._uiService.showErrorAlert(
            POPUPS.DUPLICATE_NAME.DUPLICATE_NAME_TITLE,
            POPUPS.DUPLICATE_NAME.DUPLICATE_OFFERING_DESCRIPTION,
          );
        } else {
          this._uiService.showErrorAlert(
            'Error',
            'There was an error publishing your offering',
          );
        }
        console.error('MuleSoft publish error:', err);
        this.handleErrorEvent();
      },
    });
  }

  handleDraftSave(): void {
    if (!this.validateDraft()) {
      this._uiService.showErrorAlert(
        POPUPS.DRAFT_ERROR.TITLE,
        POPUPS.DRAFT_ERROR.DESCRIPTION,
      );
      return;
    }

    const offeringUUID = this.offeringUUID();
    const offeringId = this.offeringId();
    const knowledge = this.knowledge();

    const mulesoftApplicationDTO = buildMulesoftApplicationDTO(
      offeringId,
      knowledge,
      this.formOverview,
      this.formDetails,
      this.formFunctionality,
      this.formPermissions,
      false,
    );

    this.isDraftSaving.set(true);

    const request$ = offeringUUID
      ? this._mulesoftService.editApplication(mulesoftApplicationDTO)
      : this._mulesoftService.createApplication(mulesoftApplicationDTO);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        this.isDraftSaving.set(false);
        this.formOverview.markAsPristine();
        this.formDetails.markAsPristine();
        this.formFunctionality.markAsPristine();
        this.formPermissions.markAsPristine();

        this._uiService.showSuccessToast(
          'Your main offering content has been saved',
          'Success',
        );

        this.handleDraftEvent();
        this.markFormAsSavedOrPublished();
        if (offeringUUID) {
          // Do NOT re-read this offering — the read endpoint is cache-lagged
          // right after a write and would revert the form to stale data. Overlay
          // the authoritative write response onto the cached list instead; the
          // form already holds the saved values.
          this.syncAfterSave(response);
        } else if (response?.offering?.uuid) {
          this._offeringsService.upsertDraftOptimistic(response.offering);
          this._router.navigate(
            ['/focus/offering/application', response.offering.uuid],
            { replaceUrl: true },
          );
        }
      },
      error: (err) => {
        this.isDraftSaving.set(false);
        if (isDuplicateNameError(err)) {
          this._uiService.showErrorToast(
            POPUPS.DUPLICATE_NAME.DUPLICATE_NAME_TITLE,
            POPUPS.DUPLICATE_NAME.DUPLICATE_OFFERING_DESCRIPTION,
          );
        } else {
          this._uiService.showErrorToast(
            'There was an error saving your offering',
            'Error',
          );
        }
        console.error('MuleSoft draft save error:', err);
        this.handleErrorEvent();
      },
    });
  }

  // Validation

  private validateDraft(): boolean {
    const displayName = this.formOverview.get('displayName');
    if (!displayName?.value || displayName.value.trim() === '') {
      return false;
    }

    const ownerEmail = this.formPermissions.get('ownerEmail');
    const deputyEmail = this.formPermissions.get('deputyEmail');
    const authorEmails = this.formPermissions.get('authorEmails');

    // Mark as touched so duplicate errors show
    ownerEmail?.markAsTouched();
    deputyEmail?.markAsTouched();
    authorEmails?.markAsTouched();

    if (!ownerEmail?.value || ownerEmail.value.trim() === '') {
      return false;
    }

    if (!deputyEmail?.value || deputyEmail.value.trim() === '') {
      return false;
    }

    // This ensures the duplicate validator runs for owner/deputy
    this.formPermissions.updateValueAndValidity();

    return this.formPermissions.valid;
  }

  private validateAllForms(): boolean {
    // Clear previous errors so they don't stack on multiple clicks
    this.validationErrors = [];

    // Mark all permissions controls as touched to show errors
    this.formPermissions.get('ownerEmail')?.markAsTouched();
    this.formPermissions.get('deputyEmail')?.markAsTouched();
    this.formPermissions.get('authorEmails')?.markAsTouched();
    this.formPermissions.updateValueAndValidity();

    this.markFormGroupTouched(this.formPermissions);
    this.markFormGroupTouched(this.formOverview);
    this.markFormGroupTouched(this.formDetails);
    this.markFormGroupTouched(this.formFunctionality);

    // Extract invalid control names from a FormGroup
    const collectInvalidFields = (form: any) => {
      if (form.invalid) {
        Object.keys(form.controls).forEach((key) => {
          if (form.get(key)?.invalid) {
            // Converts camelCase to separate words (e.g., 'ownerEmail' -> 'Owner Email')
            const formattedKey = key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase());
            this.validationErrors.push(formattedKey);
          }
        });
      }
    };

    collectInvalidFields(this.formPermissions);
    collectInvalidFields(this.formOverview);
    collectInvalidFields(this.formDetails);
    collectInvalidFields(this.formFunctionality);

    const isSupported = this.formDetails.get('isSupported')?.value;

    if (isSupported && this.supportChannels().length === 0) {
      this.validationErrors.push('Support Channels');
    }
    if (isSupported && this.supportHours().length === 0) {
      this.validationErrors.push('Support Hours');
    }
    if (this.installations().length === 0) {
      this.validationErrors.push('Installations');
    }
    if (this.features().length === 0) {
      this.validationErrors.push('Features');
    }

    // Return true ONLY if there are no errors collected
    return this.validationErrors.length === 0;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control && typeof control === 'object' && 'controls' in control) {
        this.markFormGroupTouched(control as FormGroup);
      }
    }
  }

  hasError(formGroup: FormGroup, fieldName: string): boolean {
    const control = formGroup.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  hasDuplicateError(fieldName: string): boolean {
    const control = this.formPermissions.get(fieldName);
    return !!(control?.touched && control?.hasError('duplicateRole'));
  }

  // Navigation
  @ViewChild('stepper') stepper: any;
  goTo(index: number): void {
    if (this.stepper) {
      this.stepper.selectedIndex = index;
    }
  }

  // Filter function
  filterBy: RdsSingleSelectFilterByFunc<string> = (
    text: string | null,
    item: RdsSingleSelectOptionComponent<string>,
  ): boolean => {
    if (text === null || text === '') {
      return true;
    }
    return item.value.toLowerCase().includes(text.toLowerCase());
  };

  // Author management
  addAuthor(event: RdsChipInputEvent<any>): void {
    const value = (event.value ?? '').trim().toLowerCase();
    if (value) {
      this.authors.update((authors) => [...authors, value]);
      this.formPermissions.patchValue({ authorEmails: this.authors() });
      this.formPermissions.updateValueAndValidity();
      this.formPermissions.get('authorEmails')?.markAsTouched();
    }
    event.chipInput?.clear();
  }

  removeAuthor(author: string): void {
    this.authors.update((authors) => authors.filter((a) => a !== author));
    this.formPermissions.patchValue({ authorEmails: this.authors() });
    this.formPermissions.updateValueAndValidity();
    this.formPermissions.get('authorEmails')?.markAsTouched();
  }

  onEditAuthor(author: string, event: RdsChipEditedEvent<string>): void {
    const value = event.value.trim().toLowerCase();
    if (!value) {
      this.removeAuthor(author);
      return;
    }

    this.authors.update((authors) => {
      const index = authors.indexOf(author);
      if (index >= 0) {
        const newAuthors = [...authors];
        newAuthors[index] = value;
        return newAuthors;
      }
      return authors;
    });
    this.formPermissions.patchValue({ authorEmails: this.authors() });
    this.formPermissions.updateValueAndValidity();
    this.formPermissions.get('authorEmails')?.markAsTouched();
  }

  // Slug validation
  private readonly slugValidationEffect = effect(() => {
    const validator = slugValidator(this.offerings(), this.offeringSlug());
    this.formOverview.controls.displayName.setValidators([
      Validators.required,
      validator,
    ]);
    this.formOverview.controls.displayName.updateValueAndValidity({
      emitEvent: false,
    });
  });

  readonly generatedSlug = computed(() => {
    const displayName = this.overviewSig().displayName;
    return displayName ? slugify(displayName) : '';
  });

  getContentStatusLabel(publishedAt?: string | null, updatedAt?: string | null): string {
    // Draft: The offering has never been published
    if (!publishedAt) {
      return 'Draft';
    }

    const pubTime = new Date(publishedAt).getTime();
    const updTime = updatedAt ? new Date(updatedAt).getTime() : 0;

    // Has changes: Published, but updated more recently
    if (updTime > pubTime) {
      return 'Has changes';
    }

    // Published: Published, with no newer updates pending
    return 'Published';
  }
}
